import { Fragment, useState } from 'react';
import styled from "styled-components"
import DisplayTotalsSubLines, { DisplayTotalsLine } from "./DisplayTotalsSubLines";
import { useStore } from "../pageView/Page";
import useTranslation from '../../hooks/useTranslation';
import RSwitcher from '../generic/RSwitcher';
import ModalForm from '../modal/ModalForm';
import resolveModalForm from '../../utils/model/resolveModalForm';
import runLogic from '../../../../common/logic/runLogic';
import { resolveVisibility } from '../../../../common/model/condition/evaluate';

const ContentWrapper = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 0rem;
    margin-bottom: 1rem;
    max-width: ${({width}) => width || 'none'};
`
const Section = styled.div`
    text-align: right;
`;

const MenuRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 0.5rem;
`;

const DisplayTotals = ({ section }) => {
    const [data, setData] = useStore( s => s)
    //logger.warn('DisplayTotals Data ', data)
    const { t } = useTranslation()

    const [openForm, setOpenForm] = useState(null)

    /*
     * A totals row can be conditional — a discount line has nothing to say on a
     * document that has no discount, and an unconditioned row would print an
     * empty amount on every other document. The predicate reads the document,
     * which is the only scope a total has.
     */
    const elements = (section.displaySection.elements || [])
        .filter(field => resolveVisibility(field, { values: data, context: {} }) === 'show')

    /*
     * The totals' own menu, beneath the last figure. Same shape as a line's:
     * items declared on the section, each shown or hidden by a condition, so
     * "add a discount" and "remove it" are one slot in two states.
     */
    const menuOptions = (section.meta.menuItems || [])
        .filter(item => resolveVisibility(item, { values: data, context: {} }) === 'show')
        .map(item => ({ type: 'item', id: item.action, name: t(item.label), ...item }))

    const handleMenu = (option) => {
        // A form-backed item opens its form, which writes to the store and
        // re-runs the chain on submit. Everything else acts here.
        if (option.modalFormRef || option.modalForm) {
            setOpenForm(option)
            return
        }

        if (option.action === 'removeDocumentDiscount') {
            // null, never '': the schema's enum admits null and rejects the
            // empty string, and null is what "there is no discount" means.
            const cleared = { ...data, documentDiscountType: null, documentDiscountValue: null }
            setData({ ...runLogic(cleared, cleared.entity), savedOn: new Date() })
        }
    }

    const modalForm = openForm ? resolveModalForm(openForm) : null

    return (
        <>
            <ContentWrapper width={section.meta.width}>
                <Section>
                    {elements.map(
                        (field, i) => {
                            return (
                                <Fragment key={i}>
                                    {field.type === 'line' &&
                                        <DisplayTotalsLine
                                            value={data[field.value]}
                                            label={field.label}
                                            size={field.scale}
                                            params={field.labelParams || []}
                                            line={data}
                                            decorator={data[field.decorator]}
                                        />
                                    }
                                    {field.type === 'array' &&
                                        <>
                                            <DisplayTotalsSubLines data={data} field={field}
                                            decorator={data[field.decorator]}/>
                                        </>}
                                </Fragment>

                            )
                        }

                    )}
                    {menuOptions.length > 0 &&
                        <MenuRow>
                            <RSwitcher
                                size='small'
                                type={section.id}
                                currentOption={t(section.meta.menuTitle)}
                                options={menuOptions}
                                handleSelect={handleMenu}
                            />
                        </MenuRow>
                    }
                </Section>
            </ContentWrapper>
            {openForm && modalForm &&
                <ModalForm
                    name='ModalForm'
                    isVisible={Boolean(openForm)}
                    setIsVisible={(visible) => { if (!visible) setOpenForm(null) }}
                    modalTitle={openForm.label}
                    id={modalForm.id}
                    groups={modalForm.elements}
                    buttons={modalForm.buttons}
                    submit={modalForm.submit}
                    section={section}
                    entity={data.entity}
                    useStore={useStore}
                    testId={`${section.id}.${openForm.action}`}
                />
            }
        </>
    )
}

export default DisplayTotals
